import React from "react";

/**
 * Selector de hora con dos dropdowns (hora 00-23 y minuto 00-59).
 * Elimina el bug de valores intermedios de <input type="time"> porque los <select>
 * nativos sólo emiten onChange al confirmar la selección con un valor completo.
 *
 * Props:
 * - value: string "HH:MM" (o "" cuando vacío)
 * - onChange: (nuevoValor: "HH:MM" | "") => void
 * - disabled: boolean
 * - minTime: string "HH:MM" (opcional). Deshabilita las horas/minutos anteriores.
 *   IMPORTANTE: sólo se aplica si allowBefore=false.
 * - allowBefore: boolean. Si true, permite seleccionar cualquier hora aunque sea
 *   menor que minTime (usado para jornada nocturna que cruza medianoche).
 * - testIdHour, testIdMin: data-testids para cada select.
 * - testId: data-testid legado (se aplica al contenedor y al select de hora).
 */
export default function TimeDropdown({
  value,
  onChange,
  disabled = false,
  minTime = "",
  allowBefore = false,
  testId,
  testIdHour,
  testIdMin,
}) {
  const [h, m] = (value || "").split(":");
  const currentH = h || "";
  const currentM = m || "";

  const [minH, minM] = (minTime || "").split(":").map((x) => (x === undefined ? NaN : Number(x)));
  const enforceMin = !allowBefore && !!minTime && Number.isFinite(minH);

  const disabledHour = (hh) => {
    if (!enforceMin) return false;
    return Number(hh) < minH;
  };

  const disabledMinute = (mm) => {
    if (!enforceMin || currentH === "") return false;
    const ch = Number(currentH);
    if (ch > minH) return false;
    if (ch < minH) return true;
    // misma hora → filtrar minutos anteriores
    return Number(mm) < (Number.isFinite(minM) ? minM : 0);
  };

  const emit = (newH, newM) => {
    if (!newH && !newM) {
      onChange("");
      return;
    }
    // Si sólo hay hora, default minuto = "00"
    const finalH = newH || "00";
    const finalM = newM || "00";
    onChange(`${finalH}:${finalM}`);
  };

  const handleHour = (e) => {
    const nh = e.target.value;
    if (nh === "") {
      onChange("");
      return;
    }
    // Si el minuto actual queda inválido por la nueva hora, se auto-corrige a "00"
    // o al mínimo permitido.
    let nm = currentM || "00";
    if (enforceMin && Number(nh) === minH && Number(nm) < (Number.isFinite(minM) ? minM : 0)) {
      nm = String(minM).padStart(2, "0");
    }
    emit(nh, nm);
  };

  const handleMin = (e) => {
    const nm = e.target.value;
    emit(currentH, nm);
  };

  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      <select
        value={currentH}
        onChange={handleHour}
        disabled={disabled}
        className="np-input py-1 px-1.5 text-xs w-14 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={testIdHour || (testId ? `${testId}-h` : undefined)}
      >
        <option value="">--</option>
        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((hh) => (
          <option key={hh} value={hh} disabled={disabledHour(hh)}>
            {hh}
          </option>
        ))}
      </select>
      <span className="text-xs text-secondary-c">:</span>
      <select
        value={currentM}
        onChange={handleMin}
        disabled={disabled || currentH === ""}
        className="np-input py-1 px-1.5 text-xs w-14 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={testIdMin || (testId ? `${testId}-m` : undefined)}
      >
        <option value="">--</option>
        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((mm) => (
          <option key={mm} value={mm} disabled={disabledMinute(mm)}>
            {mm}
          </option>
        ))}
      </select>
    </div>
  );
}
